import { PROJECT_STATUSES } from "../constants";

/**
 * Determines what financial information is required based on the project status
 * @param {string} statusName - The name of the status
 * @param {Object} project - The project object to check current financial info
 * @returns {Array} Array of required field names
 */
export const getRequiredFinancialFields = (statusName, project) => {
  const requiredFields = [];

  // Get the index of the current status
  const statusIndex = PROJECT_STATUSES.indexOf(statusName);

  if (statusIndex === -1) {
    return requiredFields; // Unknown status, no requirements
  }

  // Check if invoiced proposal is required (PO Received or greater)
  const poReceivedIndex = PROJECT_STATUSES.indexOf("PO Received");
  if (statusIndex >= poReceivedIndex) {
    // Only require if not already filled in
    if (!project?.invoiced_proposal_id || !project?.invoiced_proposal_price) {
      requiredFields.push("invoicedProposal");
    }
  }

  // Check if job costing is required (Invoice or greater)
  const invoiceIndex = PROJECT_STATUSES.indexOf("Invoice");
  if (statusIndex >= invoiceIndex) {
    // Only require if not already filled in
    if (
      !project?.job_costing_proposal_id ||
      !project?.job_costing_proposal_price
    ) {
      requiredFields.push("jobCosting");
    }
  }

  return requiredFields;
};

/**
 * Checks if a status change requires financial information validation
 * @param {string} newStatusName - The new status being set
 * @param {string} currentStatusName - The current status
 * @param {Object} project - The project object
 * @returns {boolean} True if validation is required
 */
export const requiresFinancialValidation = (
  newStatusName,
  currentStatusName,
  project,
) => {
  const requiredFields = getRequiredFinancialFields(newStatusName, project);
  return requiredFields.length > 0;
};

/**
 * Gets a user-friendly message about what financial information is required
 * @param {Array} requiredFields - Array of required field names
 * @returns {string} User-friendly message
 */
export const getFinancialRequirementMessage = (requiredFields) => {
  if (requiredFields.length === 0) {
    return "";
  }

  const requirements = [];

  if (requiredFields.includes("invoicedProposal")) {
    requirements.push("invoiced proposal and price");
  }

  if (requiredFields.includes("jobCosting")) {
    requirements.push("job costing proposal and price");
  }

  if (requirements.length === 1) {
    return `Please provide ${requirements[0]} to continue.`;
  } else if (requirements.length === 2) {
    return `Please provide ${requirements[0]} and ${requirements[1]} to continue.`;
  } else {
    const lastRequirement = requirements.pop();
    return `Please provide ${requirements.join(", ")}, and ${lastRequirement} to continue.`;
  }
};
